import React, { useRef, useState } from 'react'
import PropTypes from 'prop-types'
import gql from 'graphql-tag'
import initApollo from '@lib/apollo-init'
import Card from '@material-ui/core/Card'
import CardMedia from '@material-ui/core/CardMedia'
import CardContent from '@material-ui/core/CardContent'
import Typography from '@material-ui/core/Typography'
import Box from '@material-ui/core/Box'
import Chip from '@material-ui/core/Chip'
import { styled, makeStyles } from '@material-ui/core/styles'
import stripMarkdown from '@lib/strip-markdown'
import stripGroupEventCoachBoilerplate from '@lib/pga-coach/strip-group-event-coach-boilerplate'
import {
  getOfferingCardTagChipMuiStyle,
  getOfferingCardTagChipsStyle,
} from '@components/pga-coach-offering-card/offering-card-tag.styles'
import { DateTime } from 'luxon'
import {
  Calendar,
  CalendarCheck,
  CircleDollarSign,
  MapPin,
  COACH_PROFILE_META_ICON_PROPS,
} from '@lib/pga-coach/coach-profile-icons'
import PlaceOutlinedIcon from '@material-ui/icons/PlaceOutlined'
import CalendarTodayOutlinedIcon from '@material-ui/icons/CalendarTodayOutlined'
import EventAvailableOutlinedIcon from '@material-ui/icons/EventAvailableOutlined'
import ButtonCta from '@components/atoms/button-cta'
import { PGA_COACH_PRIVATE_LISTING_IMAGE } from '@lib/pga-coach/constants'
import { composeOptimizedImageUrl } from '@utils/composeOptimizedImageUrl'
import {
  formatCoachProfileFacilityLabel,
  getSoonestAvailabilityWindow,
  hasImminentCoachProfileAvailability,
} from '@lib/pga-coach/offering-search'
import {
  formatBookCardPrice,
  getBookCardPriceSuffix,
} from '@layouts/things-to-do/events/group-event-book-card-price'
import { resolveOfferingCtaLink } from '@lib/pga-coach/offering-cta-link'
import {
  coachProfileRowInlineMeta,
  coachProfileRowLocationMeta,
  coachProfileRowMetaEllipsisText,
} from '@lib/pga-coach/coach-profile-layout'
import SessionsAccordion from '@components/group-event-card/sessions-accordion'
import { useTrackCoachProfileEvent } from '@lib/pga-coach/coach-profile-analytics'

export const GROUP_EVENT_SESSIONS_QUERY = gql`
  query GetGroupEventSessions($slug: String) {
    groupEvents(slug: $slug) {
      id
      sessions {
        id
        startDateTime
        endDateTime
      }
    }
  }
`

const JRL_BG_IMAGE = 'https://images.ctfassets.net/56u5qdsjym8c/1yoePOUAugYt2Za7y7Zmca/394ca263cad1c11e1344efb13ed2e0e3/JRL_programs_bg.png?h=250'
const FG_BG_IMAGE = 'https://images.ctfassets.net/56u5qdsjym8c/5SWGO8vvLshekEP1mGo826/0345e17afa5d282c0bafb50174b06bf5/FG_programs_bg.png?h=250'
const JRL_LOGO = 'https://images.ctfassets.net/56u5qdsjym8c/7sy1Gw4RKBLC9d8ylCdMOs/ec28c360c5b398789a2cb0fd48525519/jr_league.svg'
const FG_LOGO = 'https://images.ctfassets.net/56u5qdsjym8c/4ECZPKJ1X5na6EW0xH33X3/48e67d481aac9b713c073f712c546516/family-golf-logo__3_.svg'

const AVAILABILITY_WINDOW_LABELS = {
  this_week: 'This Week',
  next_week: 'Next Week',
  this_month: 'This Month',
  next_month: 'Next Month',
}

const FORMAT_LOCALE = 'en-US'
/** Match pga-coach-offering-card / dateUtils: never fall back to runtime local zone. */
const DEFAULT_ZONE = 'UTC'

const toDateTime = (date, timezone) => {
  if (!date) return null
  const zone = timezone || DEFAULT_ZONE
  const dt = date instanceof Date
    ? DateTime.fromJSDate(date, { zone })
    : DateTime.fromISO(String(date), { zone, setZone: true })
  if (!dt.isValid) return null
  return dt.setZone(zone).setLocale(FORMAT_LOCALE)
}

const formatShortDate = (date, timezone) => {
  const dt = toDateTime(date, timezone)
  return dt ? dt.toFormat('ccc, LLL d') : null
}

const formatShortTime = (date, timezone) => {
  const dt = toDateTime(date, timezone)
  return dt ? dt.toFormat('h:mm a') : null
}

const formatShortDateTime = (date, timezone) => {
  const datePart = formatShortDate(date, timezone)
  const timePart = formatShortTime(date, timezone)
  if (!datePart || !timePart) return null
  return `${datePart}, ${timePart}`
}

const isSameDay = (a, b, timezone) => {
  const dayA = toDateTime(a, timezone)
  const dayB = toDateTime(b, timezone)
  return Boolean(dayA && dayB && dayA.hasSame(dayB, 'day'))
}

const getAvailabilityChipLabel = (offering) => {
  if (offering.format !== 'private') return null
  const soonestWindow = getSoonestAvailabilityWindow(offering)
  if (soonestWindow && AVAILABILITY_WINDOW_LABELS[soonestWindow]) {
    return `Has Availability ${AVAILABILITY_WINDOW_LABELS[soonestWindow]}`
  }
  return null
}

export const getScheduleDateLabel = (offering) => {
  const {
    format, startDateTime, endDateTime, nextAvailableAt, timezone,
  } = offering
  const tz = timezone || DEFAULT_ZONE

  if (format === 'private') {
    if (nextAvailableAt) {
      const label = formatShortDateTime(nextAvailableAt, tz)
      if (label) return `Next available: ${label}`
    }
    return null
  }

  if ((format === 'program' || format === 'group') && startDateTime && endDateTime) {
    if (isSameDay(startDateTime, endDateTime, tz)) {
      return formatShortDateTime(startDateTime, tz)
    }
    const start = formatShortDateTime(startDateTime, tz)
    const end = formatShortDateTime(endDateTime, tz)
    if (start && end) return `${start} - ${end}`
  }

  if (nextAvailableAt) {
    const label = formatShortDateTime(nextAvailableAt, tz)
    if (label) return `Next available: ${label}`
  }

  return null
}

const getCardPriceLabel = (offering) => {
  if (offering.priceLabel) return offering.priceLabel
  if (offering.priceInCents == null) return null
  if (offering.priceInCents === 0) return 'Free'
  return `$${(offering.priceInCents / 100).toFixed(0)}`
}

const getHorizontalFooterPrice = (offering) => {
  if (offering.priceLabel) {
    return { amount: offering.priceLabel, suffix: null }
  }
  const amount = formatBookCardPrice(offering.priceInCents)
  if (!amount) return null
  const suffix = getBookCardPriceSuffix(offering)
  return { amount, suffix }
}

const hasFamilyCupTag = (offering) => (offering.tagLabels || [])
  .some((t) => String(t).toLowerCase().includes('family cup'))

const toTitleFormat = (value = '') => value
  .split(/[\s_-]+/)
  .filter(Boolean)
  .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1).toLowerCase())
  .join(' ')

/** Same as `/golf-lessons` list cards: title-case, max 4. */
const getTagLabels = (offering) => (offering.tagLabels || [])
  .filter(Boolean)
  .slice(0, 4)
  .map(toTitleFormat)

const getRowLocationLabel = (offering) => formatCoachProfileFacilityLabel(offering)

const formatProgramMonthRange = (offering) => {
  const tz = offering.timezone || DEFAULT_ZONE
  const start = toDateTime(offering.startDateTime, tz)
  const end = toDateTime(offering.endDateTime, tz)
  if (!start || !end) return null
  return `${start.toFormat('LLL yyyy')} - ${end.toFormat('LLL yyyy')}`
}

export const isMultiSessionGroupEvent = (offering) => (
  offering.format === 'group' && offering.lessonCount > 1
)

const getRowDateLabel = (offering) => {
  if (offering.format === 'program') return formatProgramMonthRange(offering)
  const availabilityChipLabel = getAvailabilityChipLabel(offering)
  if (availabilityChipLabel) return availabilityChipLabel
  const scheduleDateLabel = getScheduleDateLabel(offering)
  if (scheduleDateLabel && isMultiSessionGroupEvent(offering)) {
    return `Starts ${scheduleDateLabel}`
  }
  return scheduleDateLabel
}

const getCardImage = (offering, width = 600) => {
  if (offering.format === 'program') return composeOptimizedImageUrl(JRL_BG_IMAGE, width)
  if (hasFamilyCupTag(offering)) return composeOptimizedImageUrl(FG_BG_IMAGE, width)
  if (offering.imageUrl) return composeOptimizedImageUrl(offering.imageUrl, width)
  return composeOptimizedImageUrl(PGA_COACH_PRIVATE_LISTING_IMAGE, width)
}

const getCardLogo = (offering) => {
  if (offering.format === 'program') return { src: JRL_LOGO, backgroundSize: '62%' }
  if (hasFamilyCupTag(offering)) return { src: FG_LOGO, backgroundSize: '80%' }
  return null
}

const LogoBadge = styled(Box)(({ theme }) => ({
  width: 64,
  height: 64,
  borderRadius: '50%',
  backgroundColor: theme.palette.common.white,
  backgroundPosition: 'center',
  backgroundRepeat: 'no-repeat',
  border: `2px solid ${theme.palette.primary.light}`,
  position: 'absolute',
  bottom: -24,
  right: 20,
  [theme.breakpoints.down('sm')]: {
    width: 52,
    height: 52,
    bottom: -20,
    right: 16,
  },
}))

const useStyles = makeStyles((theme) => ({
  card: {
    boxShadow: '0px 4px 12px rgba(0, 10, 22, 0.13)',
    borderRadius: 8,
    overflow: 'visible',
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    textDecoration: 'none',
    transition: 'box-shadow 200ms ease',
    '&:hover': {
      boxShadow: '0px 6px 20px rgba(0, 10, 22, 0.22)',
      cursor: 'pointer',
      textDecoration: 'none',
    },
  },
  mediaWrap: {
    position: 'relative',
    overflow: 'hidden',
    borderRadius: '8px 8px 0 0',
    flexShrink: 0,
  },
  media: {
    height: 130,
    [theme.breakpoints.down('sm')]: {
      height: 100,
    },
  },
  body: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    borderRadius: '0 0 8px 8px',
    backgroundColor: theme.palette.common.white,
  },
  content: {
    padding: theme.spacing(0, 3, 1.5),
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(0, 2, 1),
    },
  },
  contentHeader: {
    paddingTop: theme.spacing(1.5),
    [theme.breakpoints.down('sm')]: {
      paddingTop: theme.spacing(1),
    },
  },
  dateChip: {
    '&.MuiChip-root': {
      alignItems: 'center',
      borderRadius: 999,
      height: 'auto',
      minHeight: 24,
      marginTop: 0,
      marginBottom: 0,
      boxShadow: 'none',
      fontWeight: 600,
      fontSize: theme.typography.pxToRem(11),
      paddingTop: theme.spacing(0.375),
      paddingBottom: theme.spacing(0.375),
    },
    '& .MuiChip-label': {
      paddingLeft: theme.spacing(1),
      paddingRight: theme.spacing(1),
      paddingTop: 0,
      paddingBottom: 0,
      lineHeight: 1.2,
    },
    '& .MuiChip-iconSmall': {
      width: 16,
      height: 16,
      fontSize: 16,
      color: 'inherit',
      marginTop: 0,
      marginBottom: 0,
      marginLeft: theme.spacing(0.75),
      marginRight: theme.spacing(-0.25),
    },
  },
  dateChipImminent: {
    /**
     * Matches selected filter chips on `/golf-lessons` search
     * (`formatChipSelected` in pga-coach-search.styles.js): pale blue
     * fill, blue outline, blue label/icon.
     */
    color: theme.palette.blue.main,
    border: `1px solid ${theme.palette.blue.main}`,
    backgroundColor: '#ECF2F9',
    '& .MuiChip-iconSmall': {
      color: theme.palette.blue.main,
    },
  },
  title: {
    fontWeight: 700,
    color: '#00234B',
    marginBottom: theme.spacing(1.5),
    lineHeight: 1.2,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    minHeight: '2.4em',
  },
  metaRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(0.75),
    minWidth: 0,
  },
  metaIcon: {
    flexShrink: 0,
    color: theme.palette.gold.main,
    '&&': {
      fontSize: 16,
    },
  },
  availabilityRow: {
    display: 'flex',
    alignItems: 'center',
    paddingBottom: theme.spacing(1.5),
    [theme.breakpoints.down('sm')]: {
      paddingBottom: theme.spacing(1),
    },
  },
  metaText: {
    fontWeight: 600,
    color: theme.palette.grey.dark,
    letterSpacing: 0,
    lineHeight: 1.3,
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
  },
  cta: {
    width: '100%',
    height: 50,
    padding: 0,
    borderTop: `1px solid ${theme.palette.grey.light}`,
    flexShrink: 0,
    '&:hover': {
      color: theme.palette.offwhite.main,
      backgroundColor: theme.palette.secondary.light,
      textDecoration: 'underline',
    },
  },
  rowCard: {
    borderRadius: 12,
    border: '1px solid #dfe3ea',
    boxShadow: 'none',
    padding: 0,
    overflow: 'hidden',
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    color: 'inherit',
    textDecoration: 'none',
    backgroundColor: theme.palette.common.white,
    cursor: 'pointer',
    transition: 'border-color 150ms ease, box-shadow 150ms ease',
    '&:hover': {
      borderColor: '#9aa9bd',
      boxShadow: '0 2px 10px rgba(15, 34, 52, 0.06)',
      textDecoration: 'none',
      color: 'inherit',
    },
  },
  rowPrice: {
    color: theme.palette.secondary.main,
    fontWeight: 700,
    whiteSpace: 'nowrap',
    [theme.breakpoints.down('sm')]: {
      fontWeight: 800,
    },
  },
  rowBodyRow: {
    display: 'grid',
    gridTemplateColumns: '220px minmax(0, 1fr)',
    gap: 0,
    alignItems: 'stretch',
    flex: 1,
    minHeight: 176,
    [theme.breakpoints.down('sm')]: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'stretch',
      gap: 0,
      minHeight: 0,
    },
  },
  rowImageWrap: {
    position: 'relative',
    width: '100%',
    minHeight: 176,
    overflow: 'hidden',
    alignSelf: 'stretch',
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      height: 148,
      minHeight: 148,
      borderRadius: 0,
    },
  },
  rowImage: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    objectPosition: 'center',
    backgroundColor: '#e7eef6',
  },
  rowContent: {
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(2, 2.5),
    [theme.breakpoints.down('sm')]: {
      padding: 0,
    },
  },
  rowMiddle: {
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.75),
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(2, 2, 0),
      gap: theme.spacing(1),
    },
  },
  rowTitle: {
    margin: 0,
    color: theme.palette.secondary.dark,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    [theme.breakpoints.down('sm')]: {
      WebkitLineClamp: 3,
    },
  },
  rowDescription: {
    margin: 0,
    color: theme.palette.grey.dark,
    lineHeight: theme.typography.subtitle2.lineHeight,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    '-webkit-line-clamp': 2,
    '-webkit-box-orient': 'vertical',
  },
  rowMetaStack: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
    minWidth: 0,
  },
  rowInlineMeta: coachProfileRowInlineMeta(theme),
  rowLocationMeta: coachProfileRowLocationMeta(theme),
  rowDateMeta: {
    color: theme.palette.grey.dark,
  },
  rowLocation: coachProfileRowMetaEllipsisText(theme),
  rowMetaText: coachProfileRowMetaEllipsisText(theme),
  rowDateText: {
    ...coachProfileRowMetaEllipsisText(theme),
    fontWeight: theme.typography.fontWeightRegular,
  },
  /** Indent past the 1em meta icon + gap so sessions align with the date text. */
  rowSessions: {
    fontSize: theme.typography.subtitle2.fontSize,
    paddingLeft: `calc(1em + ${theme.spacing(0.5)}px)`,
  },
  rowSessionsToggle: {
    display: 'inline-block',
    color: theme.palette.blue.main,
  },
  rowSessionsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.25),
    paddingTop: theme.spacing(0.5),
  },
  rowSessionItem: {
    color: theme.palette.grey.dark,
    fontWeight: 400,
  },
  rowDivider: {
    display: 'none',
    [theme.breakpoints.down('sm')]: {
      display: 'block',
      width: 'auto',
      height: 1,
      backgroundColor: '#dfe3ea',
      margin: theme.spacing(1.5, 2, 0),
    },
  },
  rowFooterRow: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'nowrap',
    gap: theme.spacing(1.5),
    marginTop: 'auto',
    paddingTop: theme.spacing(1.5),
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      alignItems: 'stretch',
      flexWrap: 'nowrap',
      gap: theme.spacing(1.5),
      marginTop: 0,
      padding: theme.spacing(1.5, 2, 2),
      paddingTop: theme.spacing(1.5),
    },
  },
  rowChips: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    marginTop: 0,
    minWidth: 0,
    flex: '1 1 auto',
    [theme.breakpoints.up('md')]: {
      flex: '1 1 0',
    },
  },
  chip: {
    '&.MuiChip-root': {
      borderRadius: 999,
    },
    height: 'auto',
    minHeight: 24,
    border: 'none',
    backgroundColor: theme.palette.blue.light25,
    color: '#8a94a3',
    fontWeight: 600,
    fontSize: theme.typography.pxToRem(11),
    '& .MuiChip-label': {
      paddingLeft: theme.spacing(1),
      paddingRight: theme.spacing(1),
      paddingTop: theme.spacing(0.25),
      paddingBottom: theme.spacing(0.25),
      lineHeight: 1.2,
    },
  },
  rowCtaWrap: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: theme.spacing(1.5),
    flexShrink: 0,
    marginTop: 0,
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      marginTop: 0,
      flexDirection: 'column',
      alignItems: 'stretch',
      gap: theme.spacing(1),
    },
  },
  rowCta: {
    '&.MuiButton-root': {
      borderRadius: 999,
    },
    minHeight: 32,
    fontSize: theme.typography.pxToRem(14),
    fontWeight: 600,
    lineHeight: 1.2,
    padding: theme.spacing(0.75, 2.25),
    whiteSpace: 'nowrap',
    boxShadow: 'none',
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      minHeight: 44,
      justifyContent: 'center',
    },
  },
  horizontalCard: {
    flexDirection: 'column',
    padding: theme.spacing(2),
    cursor: 'pointer',
    transition: 'transform 180ms ease, box-shadow 180ms ease',
    '&&': {
      overflow: 'visible',
      border: `1px solid ${theme.palette.grey[200]}`,
      borderRadius: 12,
      backgroundColor: theme.palette.common.white,
      boxShadow: '0 8px 28px rgba(0, 10, 22, 0.08)',
    },
    '@media (hover: hover)': {
      '&:hover': {
        transform: 'translateY(-4px)',
        '&&': {
          boxShadow: '0 12px 32px rgba(15, 34, 52, 0.1)',
        },
      },
    },
    '&:focus-within': {
      outline: `2px solid ${theme.palette.secondary.main}`,
      outlineOffset: 2,
    },
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(1.5),
    },
  },
  horizontalBodyRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1.5),
    minWidth: 0,
  },
  horizontalImageWrap: {
    width: 112,
    height: 96,
    borderRadius: 6,
    overflow: 'hidden',
    flexShrink: 0,
    [theme.breakpoints.down('sm')]: {
      width: 80,
      height: 80,
    },
  },
  horizontalImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    objectPosition: 'center',
    backgroundColor: '#e7eef6',
  },
  horizontalMiddle: {
    flex: 1,
    minWidth: 0,
  },
  horizontalTitle: {
    fontSize: theme.typography.pxToRem(18),
    fontWeight: 700,
    color: '#00234B',
    marginBottom: theme.spacing(1),
    lineHeight: 1.2,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
  },
  horizontalDescription: {
    color: '#5f6771',
    fontSize: theme.typography.pxToRem(13),
    lineHeight: 1.35,
    marginBottom: theme.spacing(0.75),
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
  },
  horizontalMetaStack: {
    display: 'flex',
    flexDirection: 'column',
    gap: 0,
    minWidth: 0,
  },
  horizontalMetaRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    minHeight: 22,
    marginBottom: 0,
    minWidth: 0,
  },
  horizontalFooter: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: theme.spacing(1),
    marginTop: theme.spacing(1.5),
    paddingTop: theme.spacing(1.5),
    borderTop: `1px solid ${theme.palette.grey.light}`,
    minWidth: 0,
    width: '100%',
  },
  horizontalTagRow: {
    ...getOfferingCardTagChipsStyle(theme),
    width: '100%',
  },
  horizontalTagChip: getOfferingCardTagChipMuiStyle(theme),
  horizontalFooterActions: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    flexShrink: 0,
    width: '100%',
    justifyContent: 'space-between',
  },
  horizontalPriceBlock: {
    minWidth: 0,
    flex: '1 1 auto',
    display: 'flex',
    alignItems: 'center',
  },
  horizontalPrice: {
    '&&': {
      fontFamily: theme.typography.h4.fontFamily,
      fontWeight: 700,
      fontSize: theme.typography.pxToRem(20),
      letterSpacing: '-0.02em',
      lineHeight: 1.15,
      color: theme.palette.secondary.dark,
      margin: 0,
    },
  },
  horizontalPriceSuffix: {
    display: 'inline',
    marginLeft: 6,
    fontFamily: theme.typography.body1.fontFamily,
    fontWeight: 400,
    fontSize: theme.typography.pxToRem(11),
    letterSpacing: 0,
    lineHeight: 1.35,
    color: theme.palette.text.secondary,
  },
  horizontalCta: {
    minHeight: 40,
    height: 'auto',
    minWidth: 120,
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    flexShrink: 0,
    alignSelf: 'center',
  },
}))

/** Session list is not on the Algolia hit; load it from Coach Tools on first expand. */
function OfferingSessions({ slug, lessonCount, timezone }) {
  const classes = useStyles()
  const trackCoachProfileEvent = useTrackCoachProfileEvent()
  const [isExpanded, setIsExpanded] = useState(false)
  const [sessions, setSessions] = useState(null)
  const [loading, setLoading] = useState(false)
  const inFlight = useRef(false)

  const loadSessions = async () => {
    if (sessions || inFlight.current) return
    inFlight.current = true
    setLoading(true)
    try {
      const { data } = await initApollo().query({
        query: GROUP_EVENT_SESSIONS_QUERY,
        variables: { slug },
        context: { clientName: 'coach-tools' },
      })
      const next = data?.groupEvents?.[0]?.sessions || []
      if (next.length) setSessions(next)
    } catch (e) {
      // Leave `sessions` unset so the next expand retries.
    } finally {
      inFlight.current = false
      setLoading(false)
    }
  }

  const handleSetExpanded = (next) => {
    trackCoachProfileEvent('click-coach-profile-offering-sessions', {
      expanded: next,
      offering_slug: slug || null,
    })
    setIsExpanded(next)
    if (next) loadSessions()
  }

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div
      onClick={(e) => e.stopPropagation()}
      className={classes.rowSessions}
      data-testid="coach-profile-offering-sessions"
    >
      <SessionsAccordion
        sessions={sessions || []}
        sessionCount={lessonCount}
        timezone={timezone}
        isExpanded={isExpanded}
        setIsExpanded={handleSetExpanded}
        loading={loading}
        variant="subtitle2"
        classes={{
          toggle: classes.rowSessionsToggle,
          list: classes.rowSessionsList,
          session: classes.rowSessionItem,
        }}
      />
    </div>
  )
}

OfferingSessions.propTypes = {
  slug: PropTypes.string.isRequired,
  lessonCount: PropTypes.number.isRequired,
  timezone: PropTypes.string,
}

OfferingSessions.defaultProps = {
  timezone: undefined,
}

const isJoinWaitlistLabel = (offering) => (
  offering.ctaLabel === 'Join Waitlist'
  || (offering.registrationFull && offering.hasWaitlist)
)

const getOfferingCtaLabel = (offering, { isRow } = {}) => {
  if (isJoinWaitlistLabel(offering)) return 'Join Waitlist'
  if (isRow) {
    return offering.ctaType === 'register' ? 'View Details' : 'Pick a Time'
  }
  return offering.ctaLabel
    || (offering.ctaType === 'register' ? 'Register' : 'Book Now')
}

function CoachProfileOfferingCard({
  offering,
  layout = 'vertical',
  variant = 'card',
  position,
  section,
}) {
  const classes = useStyles()
  const trackCoachProfileEvent = useTrackCoachProfileEvent()
  const isRow = variant === 'row'
  const resolvedLayout = layout === 'horizontal' ? 'horizontal' : 'vertical'
  const cardImage = getCardImage(offering, isRow ? 640 : 600)
  const logo = getCardLogo(offering)
  const availabilityChipLabel = getAvailabilityChipLabel(offering)
  const scheduleDateLabel = getScheduleDateLabel(offering)
  const isImminentAvailability = hasImminentCoachProfileAvailability(offering)
  const priceLabel = getCardPriceLabel(offering)
  const horizontalFooterPrice = resolvedLayout === 'horizontal'
    ? getHorizontalFooterPrice(offering)
    : null
  const location = offering.venueName
    || [offering.city, offering.state].filter(Boolean).join(', ')
    || null
  const rowLocationLabel = getRowLocationLabel(offering)
  const rowDateLabel = getRowDateLabel(offering)
  const showSessions = isMultiSessionGroupEvent(offering) && Boolean(offering.slug)
  const rowDescription = offering.description
    ? stripMarkdown(offering.description).trim()
    : ''
  const shopTagLabels = getTagLabels(offering)
  const showPrivateChip = offering.format === 'private'
  const ctaLabel = getOfferingCtaLabel(offering, { isRow })
  const { href: ctaHref, isExternal: external } = offering.ctaUrl
    ? resolveOfferingCtaLink(offering.ctaUrl)
    : { href: null, isExternal: false }
  const descriptionText = offering.description
    ? stripMarkdown(stripGroupEventCoachBoilerplate(offering.description)).trim()
    : ''
  const tagLabels = (offering.tagLabels || []).filter(Boolean).slice(0, 4)

  const trackOfferingClick = (clickTarget) => {
    trackCoachProfileEvent('click-coach-profile-offering', {
      click_target: clickTarget,
      offering_id: offering.objectID || null,
      offering_title: offering.title || null,
      format: offering.format || null,
      cta_type: offering.ctaType || null,
      cta_label: ctaLabel,
      href: ctaHref,
      is_external: external,
      section: section || null,
      position: position ?? null,
    })
  }

  const handleCtaClick = (e) => {
    e.stopPropagation()
    trackOfferingClick('cta')
  }

  const handleCardClick = (e) => {
    if (!ctaHref) return
    const interactive = e.target.closest('a, button, [role="button"]')
    if (interactive && interactive !== e.currentTarget) return
    trackOfferingClick('card')
    if (external) {
      window.open(ctaHref, '_blank', 'noopener,noreferrer')
    } else {
      window.location.href = ctaHref
    }
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      handleCardClick(e)
    }
  }

  if (isRow) {
    return (
      <Card
        className={classes.rowCard}
        onClick={ctaHref ? handleCardClick : undefined}
      >
        <div className={classes.rowBodyRow}>
          <div className={classes.rowImageWrap}>
            <img
              className={classes.rowImage}
              src={cardImage}
              alt={offering.title}
            />
          </div>
          <div className={classes.rowContent}>
            <div className={classes.rowMiddle}>
              <Typography variant="h6" className={classes.rowTitle}>
                {offering.title}
              </Typography>
              {rowDescription ? (
                <Typography variant="body2" className={classes.rowDescription}>
                  {rowDescription}
                </Typography>
              ) : null}
              {(rowLocationLabel || rowDateLabel) ? (
                <div className={classes.rowMetaStack}>
                  {rowLocationLabel ? (
                    <Box
                      className={`${classes.rowInlineMeta} ${classes.rowLocationMeta}`}
                    >
                      <MapPin {...COACH_PROFILE_META_ICON_PROPS} />
                      <Typography
                        variant="subtitle2"
                        component="span"
                        className={classes.rowLocation}
                      >
                        {rowLocationLabel}
                      </Typography>
                    </Box>
                  ) : null}
                  {rowDateLabel ? (
                    <div
                      className={`${classes.rowInlineMeta} ${classes.rowDateMeta}`}
                    >
                      <Calendar {...COACH_PROFILE_META_ICON_PROPS} />
                      <Typography
                        variant="subtitle2"
                        component="span"
                        className={classes.rowDateText}
                      >
                        {rowDateLabel}
                      </Typography>
                    </div>
                  ) : null}
                  {showSessions ? (
                    <OfferingSessions
                      slug={offering.slug}
                      lessonCount={offering.lessonCount}
                      timezone={offering.timezone}
                    />
                  ) : null}
                </div>
              ) : null}
            </div>

            <div className={classes.rowDivider} />

            <div className={classes.rowFooterRow}>
              <Box className={classes.rowChips}>
                {showPrivateChip ? (
                  <Chip label="Private" size="small" className={classes.chip} />
                ) : null}
                {shopTagLabels.map((label) => (
                  <Chip key={label} label={label} size="small" className={classes.chip} />
                ))}
              </Box>
              {(priceLabel || ctaHref) ? (
                <div className={classes.rowCtaWrap}>
                  {priceLabel ? (
                    <Typography
                      variant="subtitle1"
                      component="span"
                      className={classes.rowPrice}
                    >
                      {priceLabel}
                    </Typography>
                  ) : null}
                  {ctaHref ? (
                    <ButtonCta
                      variant="contained"
                      color="primary"
                      size="small"
                      className={classes.rowCta}
                      onClick={handleCtaClick}
                      {...(external
                        ? { externalLink: ctaHref }
                        : { href: ctaHref })}
                    >
                      {ctaLabel}
                    </ButtonCta>
                  ) : null}
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </Card>
    )
  }

  if (resolvedLayout === 'horizontal') {
    return (
      <Card
        elevation={0}
        className={`${classes.card} ${classes.horizontalCard}`}
        onClick={ctaHref ? handleCardClick : undefined}
        role={ctaHref ? 'link' : undefined}
        tabIndex={ctaHref ? 0 : undefined}
        onKeyDown={ctaHref ? handleKeyDown : undefined}
      >
        <div className={classes.horizontalBodyRow}>
          <div className={classes.horizontalImageWrap}>
            <CardMedia
              className={classes.horizontalImage}
              image={cardImage}
              title={offering.title}
            />
            {logo && (
              <LogoBadge
                style={{
                  backgroundImage: `url(${logo.src})`,
                  backgroundSize: logo.backgroundSize,
                  width: 48,
                  height: 48,
                  bottom: -16,
                  right: 8,
                }}
              />
            )}
          </div>
          <div className={classes.horizontalMiddle}>
            {availabilityChipLabel && (
              <div className={classes.availabilityRow}>
                <Chip
                  size="small"
                  label={availabilityChipLabel}
                  icon={isImminentAvailability
                    ? <EventAvailableOutlinedIcon aria-hidden />
                    : undefined}
                  className={[
                    classes.dateChip,
                    isImminentAvailability ? classes.dateChipImminent : '',
                  ].filter(Boolean).join(' ')}
                />
              </div>
            )}
            <Typography variant="h5" className={classes.horizontalTitle}>
              {offering.title}
            </Typography>
            {descriptionText && (
              <Typography component="p" className={classes.horizontalDescription}>
                {descriptionText}
              </Typography>
            )}
            {(location || scheduleDateLabel) && (
              <div className={classes.horizontalMetaStack}>
                {location && (
                  <div className={classes.horizontalMetaRow}>
                    <PlaceOutlinedIcon className={classes.metaIcon} aria-hidden />
                    <Typography variant="caption" className={classes.metaText}>
                      {location}
                    </Typography>
                  </div>
                )}
                {scheduleDateLabel && (
                  <div className={classes.horizontalMetaRow}>
                    <CalendarTodayOutlinedIcon className={classes.metaIcon} aria-hidden />
                    <Typography variant="caption" className={classes.metaText}>
                      {scheduleDateLabel}
                    </Typography>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        {(tagLabels.length > 0 || horizontalFooterPrice || ctaHref) && (
          <div className={classes.horizontalFooter}>
            {tagLabels.length > 0 && (
              <Box className={classes.horizontalTagRow}>
                {tagLabels.map((label) => (
                  <Chip
                    key={label}
                    label={label}
                    size="small"
                    className={classes.horizontalTagChip}
                  />
                ))}
              </Box>
            )}
            <div className={classes.horizontalFooterActions}>
              {horizontalFooterPrice && (
                <Box className={classes.horizontalPriceBlock}>
                  <Typography
                    className={classes.horizontalPrice}
                    component="p"
                    variant="body1"
                  >
                    {horizontalFooterPrice.amount}
                    {horizontalFooterPrice.suffix ? (
                      <span className={classes.horizontalPriceSuffix}>
                        {horizontalFooterPrice.suffix}
                      </span>
                    ) : null}
                  </Typography>
                </Box>
              )}
              {ctaHref && (
                <ButtonCta
                  variant="text"
                  arrow
                  className={classes.horizontalCta}
                  onClick={handleCtaClick}
                  {...(external
                    ? { externalLink: ctaHref }
                    : { href: ctaHref })}
                >
                  {ctaLabel}
                </ButtonCta>
              )}
            </div>
          </div>
        )}
      </Card>
    )
  }

  return (
    <Card
      className={classes.card}
      onClick={ctaHref ? handleCardClick : undefined}
      role={ctaHref ? 'link' : undefined}
      tabIndex={ctaHref ? 0 : undefined}
      onKeyDown={ctaHref ? handleKeyDown : undefined}
    >
      <div className={classes.mediaWrap}>
        <CardMedia
          className={classes.media}
          image={cardImage}
          title={offering.title}
        />
        {logo && (
          <LogoBadge
            style={{
              backgroundImage: `url(${logo.src})`,
              backgroundSize: logo.backgroundSize,
            }}
          />
        )}
      </div>
      <div className={classes.body}>
        <CardContent className={classes.content}>
          <div className={classes.contentHeader}>
            {availabilityChipLabel && (
              <div className={classes.availabilityRow}>
                <Chip
                  size="small"
                  label={availabilityChipLabel}
                  icon={isImminentAvailability
                    ? <CalendarCheck {...COACH_PROFILE_META_ICON_PROPS} />
                    : undefined}
                  className={[
                    classes.dateChip,
                    isImminentAvailability ? classes.dateChipImminent : '',
                  ].filter(Boolean).join(' ')}
                />
              </div>
            )}
            <Typography variant="h5" className={classes.title}>
              {offering.title}
            </Typography>
          </div>
          <div>
            {location && (
              <div className={classes.metaRow}>
                <MapPin className={classes.metaIcon} {...COACH_PROFILE_META_ICON_PROPS} />
                <Typography variant="caption" className={classes.metaText}>
                  {location}
                </Typography>
              </div>
            )}
            {priceLabel && (
              <div className={classes.metaRow}>
                <CircleDollarSign className={classes.metaIcon} {...COACH_PROFILE_META_ICON_PROPS} />
                <Typography variant="caption" className={classes.metaText}>
                  {priceLabel}
                </Typography>
              </div>
            )}
            {scheduleDateLabel && (
              <div className={classes.metaRow}>
                <Calendar className={classes.metaIcon} {...COACH_PROFILE_META_ICON_PROPS} />
                <Typography variant="caption" className={classes.metaText}>
                  {scheduleDateLabel}
                </Typography>
              </div>
            )}
          </div>
        </CardContent>
        {ctaHref && (
          <ButtonCta
            variant="text"
            arrow
            className={classes.cta}
            onClick={handleCtaClick}
            {...(external
              ? { externalLink: ctaHref }
              : { href: ctaHref })}
          >
            {ctaLabel}
          </ButtonCta>
        )}
      </div>
    </Card>
  )
}

CoachProfileOfferingCard.propTypes = {
  /** 1-based index within the shop section, for analytics. */
  position: PropTypes.number,
  /** Shop section id (`private`, `group`, …), for analytics. */
  section: PropTypes.string,
  layout: PropTypes.oneOf(['vertical', 'horizontal']),
  variant: PropTypes.oneOf(['card', 'row']),
  offering: PropTypes.shape({
    objectID: PropTypes.string,
    slug: PropTypes.string,
    lessonCount: PropTypes.number,
    title: PropTypes.string,
    format: PropTypes.string,
    imageUrl: PropTypes.string,
    description: PropTypes.string,
    tagLabels: PropTypes.arrayOf(PropTypes.string),
    priceInCents: PropTypes.number,
    priceLabel: PropTypes.string,
    paymentRecurrenceSchedule: PropTypes.string,
    venueName: PropTypes.string,
    city: PropTypes.string,
    state: PropTypes.string,
    startDateTime: PropTypes.instanceOf(Date),
    endDateTime: PropTypes.instanceOf(Date),
    nextAvailableAt: PropTypes.instanceOf(Date),
    availabilityWindows: PropTypes.arrayOf(PropTypes.string),
    timezone: PropTypes.string,
    ctaUrl: PropTypes.string,
    ctaLabel: PropTypes.string,
    ctaType: PropTypes.string,
  }).isRequired,
}

CoachProfileOfferingCard.defaultProps = {
  layout: 'vertical',
  position: undefined,
  section: undefined,
}

export default CoachProfileOfferingCard
